import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AdminSidebar from '../../components/ui/AdminSidebar';
import { partnerCategoryService } from '../../services/partnerCategoryService';
import { useViewport } from '../../hooks/useMobileGestures';
import { useAuth } from '../../contexts/AuthContext';

const PartnerCategoriesManagement = () => {
  const navigate = useNavigate();
  const viewport = useViewport();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Building2',
    isVisible: true
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await partnerCategoryService?.getAll();
      setCategories(data);
    } catch (err) {
      setError(err?.message || 'Fehler beim Laden der Kategorien');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories?.filter(category =>
    category?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    category?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleAddCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      icon: 'Building2',
      isVisible: true
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category?.name || '',
      description: category?.description || '',
      icon: category?.icon || 'Building2',
      isVisible: category?.isVisible !== undefined ? category?.isVisible : true
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors?.[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.name?.trim()) {
      errors.name = 'Name ist erforderlich';
    } else if (formData?.name?.trim()?.length < 2) {
      errors.name = 'Name muss mindestens 2 Zeichen lang sein';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSaveCategory = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setError('');
      if (editingCategory) {
        await partnerCategoryService?.update(editingCategory?.id, formData);
      } else {
        await partnerCategoryService?.create(formData);
      }
      await loadCategories();
      setShowForm(false);
      setEditingCategory(null);
    } catch (err) {
      setError(err?.message || 'Fehler beim Speichern der Kategorie');
      console.error('Error saving category:', err);
    }
  };

  const handleDeleteCategory = (category) => {
    setDeletingCategory(category);
  };

  const confirmDelete = async () => {
    try {
      setError('');
      await partnerCategoryService?.delete(deletingCategory?.id);
      await loadCategories();
      setDeletingCategory(null);
    } catch (err) {
      setError(err?.message || 'Fehler beim Löschen der Kategorie');
      console.error('Error deleting category:', err);
    }
  };

  const handleToggleVisibility = async (category) => {
    try {
      setError('');
      await partnerCategoryService?.toggleVisibility(category?.id, !category?.isVisible);
      await loadCategories();
    } catch (err) {
      setError(err?.message || 'Fehler beim Ändern der Sichtbarkeit');
      console.error('Error toggling visibility:', err);
    }
  };

  // Icon options for category selection
  const iconOptions = [
    { value: 'Building2', label: 'Gebäude' },
    { value: 'Car', label: 'Auto' },
    { value: 'Wrench', label: 'Werkzeug' },
    { value: 'Zap', label: 'Blitz' },
    { value: 'Truck', label: 'LKW' },
    { value: 'Shield', label: 'Schild' },
    { value: 'Store', label: 'Geschäft' },
    { value: 'Home', label: 'Haus' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Kategorien werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />
      <div className="lg:pl-60">
        <div className="mobile-padding space-y-4 sm:space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <div className="flex-1">
                  <p className="font-medium text-destructive">Fehler</p>
                  <p className="text-sm text-destructive/80 mt-1">{error}</p>
                </div>
                <button 
                  onClick={() => setError('')}
                  className="p-1 rounded hover:bg-destructive/20 transition-smooth"
                >
                  <Icon name="X" size={16} color="var(--color-destructive)" />
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="mobile-heading text-foreground">
                Partnerkategorien
              </h1>
              <p className="mobile-body text-muted-foreground mt-1">
                Verwalten Sie Kategorien für die Partnersuche
              </p>
            </div>
            <Button
              variant="default"
              size={viewport?.isMobile ? 'default' : 'lg'}
              iconName="Plus"
              onClick={handleAddCategory}
              className="w-full sm:w-auto"
            >
              Kategorie hinzufügen
            </Button>
          </div>

          {/* Search */}
          <div className="max-w-md">
            <Input
              type="text"
              placeholder="Kategorien durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              leftIcon="Search"
            />
          </div>

          {/* Category Form Modal */}
          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-card border border-border rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="p-2 rounded-lg hover:bg-muted transition-smooth"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>

                <form onSubmit={handleSaveCategory} className="space-y-4">
                  <Input
                    label="Name"
                    type="text"
                    name="name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    placeholder="z.B. Autohaus"
                    error={formErrors?.name}
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Beschreibung
                    </label>
                    <textarea
                      name="description"
                      value={formData?.description}
                      onChange={handleInputChange}
                      placeholder="Optionale Beschreibung der Kategorie"
                      className="w-full px-4 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
                      rows={3}
                    />
                  </div>

                  <Select
                    label="Icon"
                    options={iconOptions}
                    value={formData?.icon}
                    onChange={(value) => setFormData(prev => ({ ...prev, icon: value }))}
                  />

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isVisible"
                      checked={formData?.isVisible}
                      onChange={(e) => setFormData(prev => ({ ...prev, isVisible: e?.target?.checked }))}
                      className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
                    />
                    <label htmlFor="isVisible" className="text-sm font-medium text-foreground">
                      In Partnersuche anzeigen
                    </label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      variant="default"
                      className="flex-1"
                    >
                      Speichern
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForm(false)}
                      className="flex-1"
                    >
                      Abbrechen
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deletingCategory && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 rounded-full bg-destructive/10">
                    <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Kategorie löschen?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Möchten Sie die Kategorie &quot;{deletingCategory?.name}&quot; wirklich löschen? 
                      Diese Aktion kann nicht rückgängig gemacht werden.
                    </p>
                    {deletingCategory?.partnerCount > 0 && (
                      <p className="text-sm text-destructive mt-2">
                        Achtung: {deletingCategory?.partnerCount} Partner sind dieser Kategorie zugeordnet.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    onClick={confirmDelete}
                    className="flex-1"
                  >
                    Löschen
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDeletingCategory(null)}
                    className="flex-1"
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Categories Table/Cards */}
          {viewport?.isMobile ? (
            // Mobile Card View
            <div className="space-y-4">
              {filteredCategories?.map(category => (
                <div key={category?.id} className="bg-card border border-border rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Icon name={category?.icon || 'Building2'} size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{category?.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {category?.partnerCount || 0} Partner
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleVisibility(category)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth ${
                        category?.isVisible
                          ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {category?.isVisible ? 'Sichtbar' : 'Verborgen'}
                    </button>
                  </div>

                  {category?.description && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {category?.description}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit2"
                      onClick={() => handleEditCategory(category)}
                      className="flex-1"
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleDeleteCategory(category)}
                      className="flex-1"
                    >
                      Löschen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop Table View
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left p-4 font-heading font-semibold text-sm text-foreground">Icon</th>
                    <th className="text-left p-4 font-heading font-semibold text-sm text-foreground">Name</th>
                    <th className="text-left p-4 font-heading font-semibold text-sm text-foreground">Beschreibung</th>
                    <th className="text-left p-4 font-heading font-semibold text-sm text-foreground">Partner</th>
                    <th className="text-left p-4 font-heading font-semibold text-sm text-foreground">Status</th>
                    <th className="text-right p-4 font-heading font-semibold text-sm text-foreground">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories?.map(category => (
                    <tr key={category?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                      <td className="p-4">
                        <div className="p-2 rounded-lg bg-primary/10 inline-block">
                          <Icon name={category?.icon || 'Building2'} size={20} color="var(--color-primary)" />
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-foreground">{category?.name}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground">
                          {category?.description || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm font-medium text-foreground">
                          {category?.partnerCount || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleVisibility(category)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth ${
                            category?.isVisible
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' :'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {category?.isVisible ? '✓ Sichtbar' : '✕ Verborgen'}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="p-2 rounded-lg hover:bg-muted transition-smooth"
                            title="Bearbeiten"
                          >
                            <Icon name="Edit2" size={18} color="var(--color-primary)" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category)}
                            className="p-2 rounded-lg hover:bg-error/10 transition-smooth"
                            title="Löschen"
                          >
                            <Icon name="Trash2" size={18} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* No Results */}
          {filteredCategories?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="FolderOpen" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Keine Kategorien gefunden
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'Versuchen Sie einen anderen Suchbegriff' : 'Fügen Sie Ihre erste Kategorie hinzu'}
              </p>
              {!searchQuery && (
                <Button variant="default" onClick={handleAddCategory} iconName="Plus">
                  Kategorie hinzufügen
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerCategoriesManagement;